import React from "react";
import { X, Clock, Wallet } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "./dialog";
import { Button } from "./button";
import lockIcon from "../../assets/icons/lock.svg";
import eightIcon from "../../assets/images/80eight/88-footer.svg";

interface NotificationMetadata {
  amount?: string;
  currency?: string;
  receivedAmount?: string;
  receivedCurrency?: string;
  recipient?: string;
  sender?: string;
  yourReference?: string;
  theirReference?: string;
  notes?: string;
  fee?: string;
  feeCurrency?: string;
  serviceCharge?: string;
  totalCost?: string;
  totalAmount?: string;
  totalCurrency?: string;
  price?: string;
  reason?: string;
}

interface Notification {
  _id: string;
  title?: string;
  message?: string;
  type?: string;
  icon?: string;
  category?: string;
  isRead?: boolean;
  createdAt?: string;
  metadata?: NotificationMetadata;
}

interface TransactionRow {
  label: string;
  value: string;
}

interface NotificationDetailModalProps {
  notification: Notification | null;
  isOpen: boolean;
  onClose: () => void;
  onGoToWallet?: () => void;
}

const formatDetailDate = (createdAt: string | undefined): string => {
  if (!createdAt) return "- - -";
  try {
    const date = new Date(createdAt);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const timeStr = format(date, "h:mm a");
    if (isToday) {
      return `Today at ${timeStr}`;
    }
    return `${format(date, "d MMMM yyyy")} at ${timeStr}`;
  } catch {
    return "- - -";
  }
};

const formatCurrencyValue = (
  amount: string,
  currency: string | undefined
): string => {
  if (!currency) return amount;
  if (currency === "ZAR") return `R ${amount}`;
  return `${amount} ${currency}`;
};

const buildTransactionRows = (
  metadata: NotificationMetadata | undefined
): TransactionRow[] => {
  if (!metadata) return [];
  const rows: TransactionRow[] = [];
  const currency = metadata.currency;

  if (metadata.amount && currency) {
    rows.push({
      label: "Amount",
      value: formatCurrencyValue(metadata.amount, currency),
    });
  }
  if (metadata.receivedAmount && metadata.receivedCurrency) {
    rows.push({
      label: "You received",
      value: formatCurrencyValue(
        metadata.receivedAmount,
        metadata.receivedCurrency
      ),
    });
  }
  if (metadata.recipient) {
    rows.push({ label: "Recipient", value: metadata.recipient });
  }
  if (metadata.sender) {
    rows.push({ label: "Sender", value: metadata.sender });
  }
  if (metadata.yourReference) {
    rows.push({ label: "Your reference", value: metadata.yourReference });
  }
  if (metadata.theirReference) {
    rows.push({ label: "Their reference", value: metadata.theirReference });
  }
  if (metadata.notes) {
    rows.push({ label: "Notes", value: metadata.notes });
  }
  const fee = metadata.serviceCharge ?? metadata.fee;
  if (fee !== undefined && fee !== null) {
    rows.push({
      label: "Service charged",
      value: formatCurrencyValue(fee, metadata.feeCurrency ?? currency),
    });
  }
  const total = metadata.totalCost ?? metadata.totalAmount;
  if (total !== undefined && total !== null) {
    rows.push({
      label: metadata.totalCost !== undefined ? "Total cost" : "Total amount",
      value: formatCurrencyValue(total, metadata.totalCurrency ?? currency),
    });
  }
  if (metadata.price) {
    rows.push({ label: "Price", value: metadata.price });
  }
  if (metadata.reason) {
    rows.push({ label: "Reason", value: metadata.reason });
  }

  return rows;
};

export default function NotificationDetailModal({
  notification,
  isOpen,
  onClose,
  onGoToWallet,
}: NotificationDetailModalProps) {
  if (!notification) return null;

  const statusTitle = notification.title ?? "Notification Details";
  const dateStr = formatDetailDate(notification.createdAt);
  const transactionRows = buildTransactionRows(notification.metadata);
  const hasTransactionDetails = transactionRows.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 bg-white rounded-xl border-0 shadow-2xl">
        <div className="relative p-6 pb-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <DialogTitle className="text-sm font-medium text-gray-500">
              Notification Details
            </DialogTitle>
            <DialogClose className="border border-gray-300 rounded-full p-2.5 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-4 w-4 text-gray-600" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>

          {/* Visually hidden description for accessibility */}
          <DialogDescription className="sr-only">
            Details for this notification
          </DialogDescription>

          {/* Status Title */}
          <h2 className="text-2xl font-bold text-[#33125F] leading-snug mb-3">
            {statusTitle}
          </h2>

          {/* Date */}
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-sm text-gray-400">{dateStr}</span>
          </div>

          {/* Message */}
          <p className="text-base text-gray-700 leading-relaxed mb-6">
            {notification.message}
          </p>

          {/* Transaction Details */}
          {hasTransactionDetails && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-500 mb-3">
                Transaction details
              </p>
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                {transactionRows.map((row, index) => (
                  <div
                    key={`${row.label}-${index}`}
                    className={`flex items-center justify-between px-4 py-3.5 ${
                      index !== transactionRows.length - 1
                        ? "border-b border-gray-200"
                        : ""
                    }`}
                  >
                    <span className="text-sm text-gray-500">{row.label}</span>
                    <span className="text-sm font-semibold text-gray-900 text-right">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section */}
        <div className="px-6 pb-6 space-y-3">
          <div className="flex items-center gap-3">
            <button
              className="flex-1 h-[48px] rounded-full bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
              onClick={onClose}
            >
              Done
            </button>

            <Button
              className="btn-primary flex-1 h-[48px] rounded-full text-sm font-semibold"
              onClick={onGoToWallet}
            >
              <Wallet className="w-4 h-4 mr-1.5" />
              Go to wallet
            </Button>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 pt-4 mt-2">
            <div className="flex items-center justify-center gap-2">
              <img src={lockIcon} alt="lock" className="w-3 h-3" />
              <span className="text-xs text-gray-400">Secured by</span>
              <img src={eightIcon} alt="80Eight" className="h-4" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export type { Notification, NotificationMetadata };
